import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  EventNote as JournalIcon,
  EditCalendar as RescheduleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const RU_MONTHS = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];
const RU_MONTHS_GEN = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
// Ключи дней недели в порядке dayjs (0=Вс … 6=Сб)
const WEEK_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const toMin = (s?: string) => {
  if (!s) return 0;
  const [h, m] = String(s).split(":");
  return parseInt(h, 10) * 60 + parseInt(m || "0", 10);
};
const parseWH = (wh: any) => {
  if (!wh) return null;
  if (typeof wh === "string") {
    try { return JSON.parse(wh); } catch { return null; }
  }
  return wh;
};

const signalBookingChange = () => {
  try {
    localStorage.setItem("booking_change", String(Date.now()));
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(new CustomEvent("booking_change"));
  } catch {
    // ignore
  }
};

type UserRole = "admin" | "master" | "client" | string;

interface MasterOption {
  id: number;
  full_name: string;
  phone: string;
  working_hours?: any;
}

interface BookingJournalItem {
  id: number;
  start_time: string;
  end_time: string;
  status: string;
  notes?: any;
  client: { id: number; name: string; phone: string };
  master: { id: number; full_name?: string | null; phone: string };
  service: { id: number; name: string; duration_minutes: number; base_price: number };
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case "pending":
      return "Ожидает";
    case "confirmed":
      return "Подтверждена";
    case "completed":
      return "Завершена";
    case "cancelled":
      return "Отменена";
    case "no_show":
      return "Не пришёл";
    default:
      return status;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "pending":
      return "warning" as const;
    case "confirmed":
      return "info" as const;
    case "completed":
      return "success" as const;
    case "cancelled":
      return "default" as const;
    case "no_show":
      return "error" as const;
    default:
      return "default" as const;
  }
};

const getStatusDot = (status: string) => {
  switch (status) {
    case "pending":
      return "#f59e0b";
    case "confirmed":
      return "#0ea5e9";
    case "completed":
      return "#16a34a";
    case "cancelled":
      return "#cbd5e1";
    case "no_show":
      return "#ef4444";
    default:
      return "#94a3b8";
  }
};

export default function BookingJournalPage() {
  const navigate = useNavigate();
  const userRole: UserRole = localStorage.getItem("user_role") || "client";
  const isAdmin = userRole === "admin";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";

  const today = dayjs().format("YYYY-MM-DD");
  const [month, setMonth] = useState(dayjs().startOf("month"));
  const [date, setDate] = useState(today); // выбранный день
  const [status, setStatus] = useState<string>("");
  const [masterIdFilter, setMasterIdFilter] = useState<number | "">("");

  const [monthBookings, setMonthBookings] = useState<BookingJournalItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [masters, setMasters] = useState<MasterOption[]>([]);

  const [alert, setAlert] = useState<{ show: boolean; message: string; severity: "success" | "error" }>({
    show: false,
    message: "",
    severity: "success",
  });

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingJournalItem | null>(null);

  const [newStart, setNewStart] = useState<string>("");
  const [newMasterId, setNewMasterId] = useState<number | "">("");

  const token = localStorage.getItem("token");

  const headers = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  }, [token]);

  // Сетка месяца: 6 недель по 7 дней, понедельник первый
  const gridStart = useMemo(() => {
    const firstOfMonth = month.startOf("month");
    const offset = (firstOfMonth.day() + 6) % 7; // Пн=0 … Вс=6
    return firstOfMonth.subtract(offset, "day");
  }, [month]);

  const monthMatrix = useMemo(() => {
    const days: dayjs.Dayjs[] = [];
    for (let i = 0; i < 42; i++) days.push(gridStart.add(i, "day"));
    return days;
  }, [gridStart]);

  const loadMasters = async () => {
    if (!isAdmin) return;
    try {
      const resp = await fetch(`${API_BASE}/users/masters`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        const opts: MasterOption[] = (Array.isArray(data) ? data : []).map((m: any) => ({
          id: Number(m.id),
          full_name: String(m.full_name || m.phone),
          phone: String(m.phone),
          working_hours: m.working_hours ?? null,
        }));
        setMasters(opts);
      } else {
        setMasters([]);
      }
    } catch {
      setMasters([]);
    }
  };

  const loadMonth = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      qs.set("start", gridStart.format("YYYY-MM-DDT00:00:00"));
      qs.set("end", gridStart.add(42, "day").format("YYYY-MM-DDT00:00:00"));
      if (status) qs.set("status", status);
      if (isAdmin && masterIdFilter !== "") qs.set("master_id", String(masterIdFilter));

      const resp = await fetch(`${API_BASE}/bookings/journal?${qs.toString()}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setMonthBookings(Array.isArray(data) ? data : []);
      } else {
        const err = await resp.json().catch(() => ({}));
        setAlert({ show: true, message: err?.detail || "Ошибка загрузки записей", severity: "error" });
        setMonthBookings([]);
      }
    } catch (e) {
      console.error(e);
      setAlert({ show: true, message: "Ошибка загрузки записей", severity: "error" });
      setMonthBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const doUpdateStatus = async (booking: BookingJournalItem, newStatus: string) => {
    try {
      const resp = await fetch(`${API_BASE}/bookings/${booking.id}/status`, {
        method: "PATCH",
        headers: { ...(headers || {}), "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (resp.ok) {
        setAlert({ show: true, message: "Статус обновлён", severity: "success" });
        void loadMonth();
        signalBookingChange();
      } else {
        const err = await resp.json().catch(() => ({}));
        setAlert({ show: true, message: err?.detail || "Ошибка обновления статуса", severity: "error" });
      }
    } catch {
      setAlert({ show: true, message: "Ошибка обновления статуса", severity: "error" });
    }
  };

  const doDelete = async () => {
    if (!selectedBooking) return;
    try {
      const resp = await fetch(`${API_BASE}/bookings/${selectedBooking.id}`, {
        method: "DELETE",
        headers,
      });
      if (resp.ok) {
        setAlert({ show: true, message: "Запись удалена", severity: "success" });
        setDeleteOpen(false);
        setSelectedBooking(null);
        void loadMonth();
        signalBookingChange();
      } else {
        const err = await resp.json().catch(() => ({}));
        setAlert({ show: true, message: err?.detail || "Ошибка удаления", severity: "error" });
      }
    } catch {
      setAlert({ show: true, message: "Ошибка удаления", severity: "error" });
    }
  };

  const doCancel = async () => {
    if (!selectedBooking) return;
    try {
      const resp = await fetch(`${API_BASE}/bookings/${selectedBooking.id}/cancel`, {
        method: "PATCH",
        headers,
      });
      if (resp.ok) {
        setAlert({ show: true, message: "Запись отменена", severity: "success" });
        setCancelOpen(false);
        setSelectedBooking(null);
        void loadMonth();
        signalBookingChange();
      } else {
        const err = await resp.json().catch(() => ({}));
        setAlert({ show: true, message: err?.detail || "Ошибка отмены", severity: "error" });
      }
    } catch {
      setAlert({ show: true, message: "Ошибка отмены", severity: "error" });
    }
  };

  const doReschedule = async () => {
    if (!selectedBooking) return;
    try {
      const payload: any = {
        start_time: dayjs(newStart).format("YYYY-MM-DDTHH:mm:ss"),
      };
      if (isAdmin && newMasterId !== "") {
        payload.master_id = Number(newMasterId);
      }

      const resp = await fetch(`${API_BASE}/bookings/${selectedBooking.id}/reschedule`, {
        method: "PATCH",
        headers: { ...(headers || {}), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        setAlert({ show: true, message: "Запись перенесена", severity: "success" });
        setRescheduleOpen(false);
        setSelectedBooking(null);
        void loadMonth();
        signalBookingChange();
      } else {
        const err = await resp.json().catch(() => ({}));
        setAlert({ show: true, message: err?.detail || "Ошибка переноса", severity: "error" });
      }
    } catch {
      setAlert({ show: true, message: "Ошибка переноса", severity: "error" });
    }
  };

  useEffect(() => {
    void loadMasters();
  }, [isAdmin]);

  useEffect(() => {
    void loadMonth();
  }, [gridStart, status, masterIdFilter, isAdmin]);

  // Группировка записей по дням
  const bookingsByDate = useMemo(() => {
    const m = new Map<string, BookingJournalItem[]>();
    for (const b of monthBookings) {
      const k = dayjs(b.start_time).format("YYYY-MM-DD");
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(b);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (a.start_time < b.start_time ? -1 : 1));
    }
    return m;
  }, [monthBookings]);

  const dayRows = useMemo(() => bookingsByDate.get(date) || [], [bookingsByDate, date]);

  const openReschedule = (b: BookingJournalItem) => {
    setSelectedBooking(b);
    setNewStart(dayjs(b.start_time).format("YYYY-MM-DDTHH:mm"));
    setNewMasterId(isAdmin ? b.master.id : "");
    setRescheduleOpen(true);
  };

  const openCancel = (b: BookingJournalItem) => {
    setSelectedBooking(b);
    setCancelOpen(true);
  };

  const openDelete = (b: BookingJournalItem) => {
    setSelectedBooking(b);
    setDeleteOpen(true);
  };

  const selectedDateTitle = useMemo(() => {
    const d = dayjs(date);
    const wd = WEEKDAYS[(d.day() + 6) % 7];
    return `${d.date()} ${RU_MONTHS_GEN[d.month()]} ${d.year()}, ${wd}`;
  }, [date]);

  // Вместимость дня = сумма 30-мин слотов рабочих мастеров в этот день недели
  const capacityForWeekday = (weekdayKey: string) => {
    const relevant = masterIdFilter !== "" ? masters.filter((m) => m.id === Number(masterIdFilter)) : masters;
    let slots = 0;
    for (const m of relevant) {
      const d = parseWH(m.working_hours)?.[weekdayKey];
      if (d?.works) {
        slots += Math.max(0, Math.round((toMin(d.close) - toMin(d.open)) / 30));
      }
    }
    return slots;
  };

  // Загрузка дня: записи / вместимость → цвет (зелёный→жёлтый→красный)
  const dayLoad = (key: string, day: dayjs.Dayjs) => {
    const items = (bookingsByDate.get(key) || []).filter((b) => b.status !== "cancelled");
    const booked = items.length;
    const cap = capacityForWeekday(WEEK_KEYS[day.day()]);
    const ratio = cap > 0 ? booked / cap : null;
    return { booked, cap, ratio };
  };
  const loadTint = (booked: number, ratio: number | null) => {
    let level: "free" | "low" | "mid" | "high";
    if (booked === 0) level = "free";
    else if (ratio !== null) level = ratio < 0.4 ? "low" : ratio < 0.8 ? "mid" : "high";
    else level = booked <= 2 ? "low" : booked <= 4 ? "mid" : "high";

    if (isDark) {
      switch (level) {
        case "free":
          return { bg: "rgba(255,255,255,0.03)", bar: "#475569", fg: theme.palette.text.secondary };
        case "low":
          return { bg: "rgba(255,255,255,0.07)", bar: "#64748b", fg: theme.palette.text.secondary };
        case "mid":
          return { bg: "#3a321f", bar: "#d4a017", fg: "#e6c15a" };
        default:
          return { bg: "#3b2026", bar: "#ef4444", fg: "#f1707f" };
      }
    }
    switch (level) {
      case "free":
        return { bg: "#f8fafc", bar: "#cbd5e1", fg: "#94a3b8" };
      case "low":
        return { bg: "#eef2f7", bar: "#94a3b8", fg: "#64748b" };
      case "mid":
        return { bg: "#fff3cd", bar: "#f59e0b", fg: "#b45309" };
      default:
        return { bg: "#f8d7da", bar: "#ef4444", fg: "#b91c1c" };
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Header title="Журнал записей" />

      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: "auto" }}>
        {/* Заголовок + фильтры */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
          <IconButton
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            aria-label="Назад"
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <JournalIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700, mr: "auto" }}>
            Журнал записей
          </Typography>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Статус</InputLabel>
            <Select value={status} label="Статус" onChange={(e) => setStatus(String(e.target.value))}>
              <MenuItem value="">Все статусы</MenuItem>
              <MenuItem value="pending">Ожидает</MenuItem>
              <MenuItem value="confirmed">Подтверждена</MenuItem>
              <MenuItem value="completed">Завершена</MenuItem>
              <MenuItem value="cancelled">Отменена</MenuItem>
            </Select>
          </FormControl>

          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Мастер</InputLabel>
              <Select
                value={masterIdFilter}
                label="Мастер"
                onChange={(e) => {
                  const v = e.target.value;
                  setMasterIdFilter(v === "" ? "" : Number(v));
                }}
              >
                <MenuItem value="">Все мастера</MenuItem>
                {masters.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Большой календарь месяца */}
        <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {/* Навигация по месяцам */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <IconButton onClick={() => setMonth((m) => m.subtract(1, "month"))} aria-label="Предыдущий месяц">
                <ChevronLeftIcon />
              </IconButton>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {RU_MONTHS[month.month()]} {month.year()}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setMonth(dayjs().startOf("month"));
                    setDate(today);
                  }}
                  sx={{ textTransform: "none", minWidth: 0, py: 0 }}
                >
                  Сегодня
                </Button>
              </Box>
              <IconButton onClick={() => setMonth((m) => m.add(1, "month"))} aria-label="Следующий месяц">
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Заголовки дней недели */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: { xs: 0.5, sm: 1 }, mb: 1 }}>
              {WEEKDAYS.map((w, i) => (
                <Typography
                  key={w}
                  variant="caption"
                  sx={{ textAlign: "center", fontWeight: 700, color: i >= 5 ? "text.disabled" : "text.secondary" }}
                >
                  {w}
                </Typography>
              ))}
            </Box>

            {/* Сетка дней */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: { xs: 0.5, sm: 1 } }}>
              {monthMatrix.map((day) => {
                const key = day.format("YYYY-MM-DD");
                const inMonth = day.month() === month.month();
                const isToday = key === today;
                const isSelected = key === date;
                const load = dayLoad(key, day);
                const tint = loadTint(load.booked, load.ratio);

                return (
                  <Box
                    key={key}
                    onClick={() => setDate(key)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "10px",
                      p: { xs: 0.5, sm: 1 },
                      minHeight: { xs: 54, sm: 80 },
                      display: "flex",
                      flexDirection: "column",
                      border: isToday && !isSelected ? "2px solid" : "1px solid",
                      borderColor: isSelected ? "primary.main" : isToday ? "primary.main" : "divider",
                      bgcolor: isSelected ? "primary.main" : inMonth ? tint.bg : "background.paper",
                      color: isSelected ? "primary.contrastText" : "text.primary",
                      opacity: inMonth ? 1 : 0.45,
                      transition: "background-color .15s, border-color .15s",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: isToday || isSelected ? 800 : 600, lineHeight: 1 }}
                      >
                        {day.date()}
                      </Typography>
                      {load.booked > 0 && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 800,
                            lineHeight: 1,
                            color: isSelected ? "primary.contrastText" : tint.fg,
                          }}
                        >
                          {load.booked}
                        </Typography>
                      )}
                    </Box>

                    {/* Полоса загрузки дня */}
                    <Box sx={{ mt: "auto" }}>
                      {load.ratio !== null ? (
                        <Box
                          sx={{
                            height: 5,
                            borderRadius: 3,
                            bgcolor: isSelected
                              ? "rgba(255,255,255,0.3)"
                              : isDark
                              ? "rgba(255,255,255,0.10)"
                              : "rgba(0,0,0,0.06)",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${Math.min(100, Math.round(load.ratio * 100))}%`,
                              bgcolor: isSelected ? "rgba(255,255,255,0.9)" : tint.bar,
                            }}
                          />
                        </Box>
                      ) : load.booked > 0 ? (
                        <Typography sx={{ fontSize: 10, lineHeight: 1, color: isSelected ? "inherit" : tint.fg }}>
                          {load.booked} зап.
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Легенда загрузки */}
            <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap", justifyContent: "center" }}>
              {[
                { c: "#94a3b8", t: "Свободно" },
                { c: "#f59e0b", t: "Заполняется" },
                { c: "#ef4444", t: "Занято" },
              ].map((x) => (
                <Box key={x.t} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: x.c }} />
                  <Typography variant="caption" color="text.secondary">{x.t}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Записи выбранного дня */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {selectedDateTitle}
            </Typography>
            <Chip label={dayRows.length} size="small" sx={{ height: 22, fontWeight: 700, bgcolor: "action.selected" }} />
          </Box>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => navigate("/booking")}>
            Новая запись
          </Button>
        </Box>

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            {dayRows.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
                <JournalIcon sx={{ fontSize: 44, opacity: 0.25, mb: 1 }} />
                <Typography>{loading ? "Загрузка…" : "На этот день записей нет"}</Typography>
              </Box>
            ) : isMobile ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {dayRows.map((b) => (
                  <Card key={b.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {dayjs(b.start_time).format("HH:mm")}–{dayjs(b.end_time).format("HH:mm")}
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {b.client.name}
                          </Typography>
                          {isAdmin && (
                            <Typography variant="body2" color="text.secondary">
                              {b.client.phone}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {b.service.name}
                          </Typography>
                          {isAdmin && (
                            <Typography variant="body2" color="text.secondary">
                              Мастер: {b.master.full_name || b.master.phone}
                            </Typography>
                          )}
                        </Box>
                        <Chip size="small" label={getStatusLabel(b.status)} color={getStatusColor(b.status)} />
                      </Box>

                      <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1} sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => void doUpdateStatus(b, "confirmed")}
                          disabled={b.status === "cancelled" || b.status === "completed" || b.status === "confirmed"}
                        >
                          Подтв.
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => void doUpdateStatus(b, "completed")}
                          disabled={b.status === "cancelled" || b.status === "completed"}
                        >
                          Готово
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => void doUpdateStatus(b, "no_show")}
                          disabled={b.status === "cancelled" || b.status === "completed" || b.status === "no_show"}
                        >
                          Не пришёл
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<RescheduleIcon />}
                          onClick={() => openReschedule(b)}
                          disabled={b.status === "cancelled"}
                        >
                          Перенос
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => openCancel(b)}
                          disabled={b.status === "cancelled"}
                        >
                          Отмена
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => openDelete(b)}
                            sx={{ gridColumn: "1 / -1" }}
                          >
                            Удалить
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Время</TableCell>
                      <TableCell>Клиент</TableCell>
                      <TableCell>Услуга</TableCell>
                      {isAdmin && <TableCell>Мастер</TableCell>}
                      <TableCell>Статус</TableCell>
                      <TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dayRows.map((b) => (
                      <TableRow key={b.id} hover>
                        <TableCell>
                          {dayjs(b.start_time).format("HH:mm")}–{dayjs(b.end_time).format("HH:mm")}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {b.client.name}
                          </Typography>
                          {isAdmin && (
                            <Typography variant="caption" color="text.secondary">
                              {b.client.phone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{b.service.name}</TableCell>
                        {isAdmin && <TableCell>{b.master.full_name || b.master.phone}</TableCell>}
                        <TableCell>
                          <Chip size="small" label={getStatusLabel(b.status)} color={getStatusColor(b.status)} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ mr: 1 }}
                            onClick={() => void doUpdateStatus(b, "confirmed")}
                            disabled={b.status === "cancelled" || b.status === "completed" || b.status === "confirmed"}
                          >
                            Подтв.
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ mr: 1 }}
                            onClick={() => void doUpdateStatus(b, "completed")}
                            disabled={b.status === "cancelled" || b.status === "completed"}
                          >
                            Готово
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            sx={{ mr: 1 }}
                            onClick={() => void doUpdateStatus(b, "no_show")}
                            disabled={b.status === "cancelled" || b.status === "completed" || b.status === "no_show"}
                          >
                            Не пришёл
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ mr: 1 }}
                            startIcon={<RescheduleIcon />}
                            onClick={() => openReschedule(b)}
                            disabled={b.status === "cancelled"}
                          >
                            Перенести
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => openCancel(b)}
                            disabled={b.status === "cancelled"}
                          >
                            Отменить
                          </Button>
                          {isAdmin && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ ml: 1 }}
                              startIcon={<DeleteIcon />}
                              onClick={() => openDelete(b)}
                            >
                              Удалить
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Перенести запись</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Новая дата/время"
              type="datetime-local"
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            {isAdmin && (
              <FormControl fullWidth>
                <InputLabel>Мастер</InputLabel>
                <Select
                  value={newMasterId}
                  label="Мастер"
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewMasterId(v === "" ? "" : Number(v));
                  }}
                >
                  {masters.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.full_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={() => void doReschedule()} disabled={!newStart}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Удалить запись?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Запись будет удалена без возможности восстановления.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Отмена</Button>
          <Button color="error" variant="contained" onClick={() => void doDelete()}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Отменить запись?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Запись будет помечена как отменённая.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Нет</Button>
          <Button color="error" variant="contained" onClick={() => void doCancel()}>
            Да, отменить
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={alert.show} autoHideDuration={6000} onClose={() => setAlert({ ...alert, show: false })}>
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, show: false })} sx={{ width: "100%" }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
